import { Component, OnInit, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../shared/toastr.service";
import { take, takeUntil } from "rxjs/operators";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Subject } from "rxjs";
import { Signature } from "./signatures.interface";
import {
  DuplicateSignatureAction,
  ReinstallSignaturesAction,
  RemoveSignatureAction,
  SetDefaultSignatureAction,
  ReorderSignaturesAction,
  GetSignaturesAction,
} from "./signatures.actions";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { SignatureChoiceDialogComponent } from "../shared/signature-choice-dialog/signature-choice-dialog.component";
import { SignatureChoice } from "../shared/signature-choice-dialog/signature-choice-dialog.interface";
import { StartCheckingForInstallationInProgressAction } from "../signature-installation-state/signature-installation-state.actions";
import { StoreExtendedService } from "../shared/store-extended.service";
import { AuthService } from "../auth/auth.service";
import { scrollToElementById } from "../shared/utils/window.utils";
import { SignaturesService } from "./signatures.service";
import { getSignaturesSelector } from "./signatures.reducer";

@Component({
  selector: "app-signatures",
  templateUrl: "./signatures.component.html",
  styleUrls: ["./signatures.component.scss"],
  standalone: false,
})
export class SignaturesComponent implements OnInit, OnDestroy {
  signatures: Signature[] = [];
  signatureWrappers: Signature[] = [];
  defaultSignatureSet: boolean;
  isDragging = false;
  hoveredIndex: number | null = null;
  loading = true;
  private wasDragStarted = false;
  private destroy$ = new Subject<void>();
  private useLazyLoading = true;

  constructor(
    private dialog: MatDialog,
    private store: StoreExtendedService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    public auth: AuthService,
    private service: SignaturesService,
    private cdr: ChangeDetectorRef,
  ) {}

  redirectToGenerator() {
    const appUser = this.auth.appUser;
    if (appUser) {
      this.auth.getGeneratorToken(appUser).subscribe(
        (token) => {
          const url = `https://generator.gsignature.com/?token=${token}`;
          window.open(url, "_blank");
        },
        (error) => {
          console.error("Error generating token:", error);
        },
      );
    } else {
      console.error("appUser not available");
    }
  }

  ngOnInit(): void {
    this.loadSignatureWrappers();
    this.subscribeToStoreChanges();
    this.store.dispatch(new GetSignaturesAction());

    document.addEventListener("click", (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const card = target.closest(".signature-card[id]");
      if (
        card &&
        !this.wasDragStarted &&
        !target.closest("button, a, mat-icon")
      ) {
        this.goToEditSignature(card.id);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByTemplateId(_: number, item: Signature): string {
    return item.templateId ?? "";
  }

  dragStarted(): void {
    this.wasDragStarted = true;
    this.isDragging = true;
    this.hoveredIndex = null;
  }

  dragEnded(): void {
    this.isDragging = false;
    setTimeout(() => (this.wasDragStarted = false), 250);
  }

  onWrapperEnter(idx: number): void {
    if (this.isDragging) {
      this.hoveredIndex = idx;
    }
  }

  onWrapperLeave(idx: number): void {
    if (this.isDragging && this.hoveredIndex === idx) {
      this.hoveredIndex = null;
    }
  }

  drop(event: CdkDragDrop<Signature[]>): void {
    const draggedSig = event.item.data as Signature;
    const draggedKey = draggedSig.templateId ?? "";

    if (this.hoveredIndex != null) {
      const from = this.signatures.findIndex(
        (s) => (s.templateId ?? "") === draggedKey,
      );
      let to = this.hoveredIndex;

      if (from < to) {
        to--;
      }

      this.signatures.splice(from, 1);
      this.signatures.splice(to, 0, draggedSig);
    } else if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.signatures, event.previousIndex, event.currentIndex);
    }

    this.store.dispatch(
      new ReorderSignaturesAction(
        this.signatures
          .map((s) => s.templateId)
          .filter((templateId): templateId is string => Boolean(templateId)),
      ),
    );

    this.hoveredIndex = null;
    this.isDragging = false;
  }

  copySignatureToClipboard(signature: Signature): void {
    const templateId = signature.templateId?.trim();
    if (!templateId) {
      return;
    }
    this.service.byId(templateId).subscribe({
      next: (template: Signature) => {
        if (template.content) {
          void navigator.clipboard.writeText(template.content);
          this.toastr.success(
            this.translate.instant("employee.copySignatureSuccess") as string,
          );
        }
      },
      error: () => {
        this.toastr.error(this.translate.instant("error") as string);
      },
    });
  }

  duplicateSignature(templateId: string): void {
    this.store
      .dispatchAndWaitForResult<string>(
        new DuplicateSignatureAction(templateId),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (
          result.success &&
          result.payload &&
          typeof result.payload === "string" &&
          !this.signatures.find((s) => s.templateId === result.payload)
        ) {
          this.service.byId(result.payload).subscribe({
            next: (template: Signature) => {
              this.signatures.push(template);
              this.defaultSignatureSet = this.signatures.some(
                (s) => s.defaultForNewEmployee,
              );
              this.cdr.detectChanges();
            },
          });
        }
      });
  }

  removeSignature(templateId: string): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "signatures.deleteConfirmationText",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm: boolean) => {
        if (confirm) {
          this.store
            .dispatchAndWaitForResult<string>(
              new RemoveSignatureAction(templateId),
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
              if (result.success) {
                const index = this.signatures.findIndex(
                  (s) => s.templateId === templateId,
                );
                if (index !== -1) {
                  this.signatures.splice(index, 1);
                  this.defaultSignatureSet = this.signatures.some(
                    (s) => s.defaultForNewEmployee,
                  );
                  this.cdr.detectChanges();
                }
              }
            });
        }
      });
  }

  goToEditSignature(templateId: string): void {
    void this.router.navigate(["signatures", templateId, "edit"]);
  }

  goToDetailsSignature(templateId: string): void {
    void this.router.navigate(["signatures", templateId]);
  }

  setDefault(signature: Signature): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "signatures.default.setDefaultConfirmation",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm: boolean) => {
        if (confirm) {
          const templateId = signature.templateId?.trim();
          if (!templateId) {
            return;
          }
          void this.store
            .dispatchAndWaitForResult(new SetDefaultSignatureAction(templateId))
            .subscribe((result) => {
              if (!result.success) {
                return;
              }
              this.signatures = this.signatures.map((item) => ({
                ...item,
                defaultForNewEmployee: item.templateId === signature.templateId,
              }));
              this.defaultSignatureSet = true;
              this.cdr.detectChanges();
              this.toastr.success(
                this.translate.instant(
                  "signatures.default.setDefaultSuccess",
                ) as string,
              );
            });
        }
      });
  }

  removeDefault(_signature: Signature): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "signatures.default.unsetDefaultConfirmation",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm: boolean) => {
        if (confirm) {
          void this.store
            .dispatchAndWaitForResult(new SetDefaultSignatureAction(null))
            .subscribe((result) => {
              if (!result.success) {
                return;
              }
              this.signatures = this.signatures.map((item) => ({
                ...item,
                defaultForNewEmployee: false,
              }));
              this.defaultSignatureSet = false;
              this.cdr.detectChanges();
              this.toastr.success(
                this.translate.instant(
                  "signatures.default.unsetDefaultSuccess",
                ) as string,
              );
            });
        }
      });
  }

  reinstallSignatures(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant(
            "signatures.reinstallConfirmation",
          ) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.store.dispatch(
            new StartCheckingForInstallationInProgressAction(),
          );
          this.store.dispatch(new ReinstallSignaturesAction());
        }
      });
  }

  navigateToAddon(section: "links" | "banners" | "disclaimers"): void {
    void this.router.navigate(["/addons"], { fragment: section });
  }

  goToEditSignatureWithDomainRules(templateId: string): void {
    void this.router.navigate(["signatures", templateId, "edit"], {
      queryParams: { domainRules: "true" },
    });
  }

  goToDetailsWithDomainRules(templateId: string, event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();
    void this.router.navigate(["signatures", templateId], {
      queryParams: { domainRules: "true" },
      fragment: "domain-rules",
    });
  }

  getDomainRulesTooltip(domainRules: string[]): string {
    if (!domainRules || domainRules.length === 0) {
      return "";
    }
    return this.translate.instant("signatures.domainRulesTooltip", {
      domains: domainRules.join(", "),
    }) as string;
  }

  hasDomainRulesInstallation(signature: Signature): boolean {
    return (
      signature.domainRulesMatchMode === "INSTALL_ANY" ||
      (signature.domainRules?.length ?? 0) > 0
    );
  }

  openSignatureChoiceDialog(): void {
    const dialogRef = this.dialog.open(SignatureChoiceDialogComponent, {
      width: "500px",
      disableClose: false,
      panelClass: "app-dialog-panel",
    });

    dialogRef.afterClosed().subscribe((result: SignatureChoice | undefined) => {
      if (result === "generator") {
        this.redirectToGenerator();
      } else if (result === "manual") {
        void this.router.navigate(["/signatures/custom/create"]);
      }
    });
  }

  hasInstalledSignatures(): boolean {
    return this.signatures.some(
      (signature) =>
        signature.numberOfInstallations > 0 ||
        signature.numberOfAliases > 0 ||
        signature.numberOfInternalInstallations > 0 ||
        signature.numberOfInternalAliases > 0,
    );
  }

  private subscribeToStoreChanges(): void {
    this.store
      .select(getSignaturesSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((storeSignatures: Signature[] | null) => {
        if (!this.useLazyLoading) {
          if (storeSignatures && storeSignatures.length > 0) {
            this.signatures = [...storeSignatures];
            this.defaultSignatureSet = this.signatures.some(
              (s) => s.defaultForNewEmployee,
            );
            this.loading = false;
            this.cdr.detectChanges();
          } else if (storeSignatures && storeSignatures.length === 0) {
            this.signatures = [];
            this.defaultSignatureSet = false;
            this.loading = false;
            this.cdr.detectChanges();
          }
        }
      });
  }

  private loadSignatureWrappers(): void {
    this.signatures = [{ templateId: "", isPlaceholder: true } as Signature];
    this.loading = true;
    this.cdr.detectChanges();
    this.loadLazyTemplates(0, 30);
  }

  private loadLazyTemplates(offset: number, limit: number): void {
    this.service.getLazyLoadingTemplates(offset, limit).subscribe({
      next: (templates: Signature[]) => {
        if (offset === 0) {
          this.signatures = templates;
          this.defaultSignatureSet = this.signatures.some(
            (s) => s.defaultForNewEmployee,
          );
          this.route.fragment
            ?.pipe(take(1))
            .subscribe((f) => f && scrollToElementById(f));
          this.loading = false;
        } else {
          this.signatures.splice(
            this.signatures.findIndex((s) => s.isPlaceholder),
            1,
          );
          this.signatures.push(...templates);
        }

        const firstTemplate = templates[0];
        const hasMore =
          firstTemplate &&
          "hasMore" in firstTemplate &&
          (firstTemplate as { hasMore?: boolean }).hasMore;
        if (hasMore) {
          this.signatures.push({
            templateId: "",
            isPlaceholder: true,
          } as Signature);
        }

        this.cdr.detectChanges();
        if (this.signatures.some((s) => s.isPlaceholder)) {
          this.loadLazyTemplates(offset + limit, limit);
        }
      },
      error: () => {
        if (offset === 0) {
          this.signatures = [];
          this.defaultSignatureSet = false;
        } else {
          this.signatures = this.signatures.filter((s) => !s.isPlaceholder);
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }
}
